/**
 * The one in-memory store behind Agent Access & Protection. Every read and
 * write of the feature goes through it (via `mockRegistry`), so the header,
 * the overview, both inventories, the drawers and the wizard can never
 * disagree. The seeded Customer Support Agent reads the fixture in `seed.ts`;
 * every agent's estate side (services, sources, readiness) is re-read from the
 * estate store on every call, and a manual MCP connection is written there, so
 * the estate's screens and this tab share one record.
 *
 * Nothing here talks to a backend, and nothing here decides effective policy:
 * the assessment is a labelled simulation (`evaluate.ts`).
 */
import { authoredAgentPolicyStore } from "@/api/mock-data/agent-estate/authoredPolicies";
import { onEstateReset } from "@/api/mock-data/agent-estate/resetListeners";
import { agentEstateStore } from "@/api/mock-data/agent-estate/store";
import type {
  AgentPolicyCoverage,
  AgentPolicyRecommendation,
  ApplyPoliciesRequest,
  ApplyPoliciesResult,
  EnforcementReadiness,
  EnforcementReadinessCounts,
  PolicyApplicationPreview,
  PolicyReview,
  PolicyReviewQuery,
} from "@/api/temp/AgentEstateService";
import type {
  AgentProtection,
  AgentProtectionQuery,
  AgentResourceLink,
  AssociateResourcesRequest,
  AssociationResult,
  DataSourceCatalog,
  DeployChangesRequest,
  DeploymentError,
  DeploymentOperation,
  DeploymentRun,
  GatewayCheckRequest,
  GatewayPath,
  McpPolicy,
  NativePolicy,
  NativeRole,
  ProtectionAssessment,
  ProtectionCandidates,
  ProtectionExpectation,
  ProtectionRecommendationQuery,
  ProtectionRule,
  ProtectionScenario,
  RegisteredDataSource,
  RegisteredMcpService,
  RemoveAssociationRequest,
  RetryFailedRequest,
  SaveNativePolicyRequest,
  ScenarioRequest,
  UpdateAssociationRequest,
  VerifyMappingRequest,
} from "@/api/temp/AgentProtectionService";
import {
  AGENT_SESSION_KEYS,
  agentSessionScope,
  isRecord,
  readAgentSession,
  registerAgentSessionReset,
  writeAgentSession,
} from "@/libs/agentSession";
import { isInvestmentBankingDemo } from "@/libs/investmentBankingDemo";
import {
  type EstateView,
  fromEstate,
  joinProtection,
  linkKey,
  toRegistered,
} from "@/pages/AgentEstate/Details/accessProtection/projection";
import { evaluateExpectations } from "./evaluate";
import {
  DATA_SOURCE_OBJECTS,
  DATA_SOURCE_REGISTRY,
  EXPECTATIONS,
  GATEWAY_PATHS,
  IDS,
  isSeededAgentId,
  LINKS,
  MCP_REGISTRY,
  NATIVE_POLICIES,
  NATIVE_ROLES,
  MCP_POLICIES,
  MCP_RULES,
  RECOMMENDATIONS,
  SEED_ASSESSED_AT,
  SEED_NOW,
} from "./seed";

/**
 * What the store owns for one agent. Everything the estate knows - the agent's
 * name, the MCP services it reaches (discovered or manually connected), the
 * data sources behind them, its enforcement readiness - is re-derived from the
 * estate on every read (`EstateView`), so a later estate change (readiness,
 * Kill Switch, a newly connected service) is never shadowed by a stale copy.
 */
interface AgentState {
  environmentId: string;
  agentId: string;
  seeded: boolean;
  /**
   * Links this store owns: the seeded fixture's links, manual data-source
   * links, and an administrator's edits (access path, note) to a derived
   * data-source link. A manual MCP link to a registry server is the estate's
   * (`agentEstateStore.bindMcpServers`), never stored here.
   */
  links: AgentResourceLink[];
  nativePolicies: NativePolicy[];
  mcpPolicies: McpPolicy[];
  mcpRules: ProtectionRule[];
  expectations: ProtectionExpectation[];
  assessment: ProtectionAssessment;
  /** Gateway paths recorded here (fixture, checks); the rest derive. */
  gatewayPaths: GatewayPath[];
  lastRun?: DeploymentRun;
  scenario: ProtectionScenario;
  /**
   * Applied authored MCP policies (`id@version`) already reported by a
   * deployment run; anything applied since is that run's "requested changes".
   */
  reportedPolicyVersions: string[];
}

const MINUTE = 60 * 1000;
const clone = <T>(value: T): T => structuredClone(value);

/** Deterministic demo clock: starts at the fixture time, one minute per write. */
class DemoClock {
  private tick = 0;
  now(): string {
    this.tick += 1;
    return new Date(Date.parse(SEED_NOW) + this.tick * MINUTE).toISOString();
  }
  reset() {
    this.tick = 0;
  }
  get value() {
    return this.tick;
  }
  set value(next: number) {
    this.tick = next;
  }
}

const unique = <T>(items: T[]) => [...new Set(items)];

const invalid = (message: string): never => {
  throw new Error(message);
};

const FIXTURE_MCP_IDS = new Set(MCP_REGISTRY.map(service => service.id));

const ownedState = (
  environmentId: string,
  agentId: string,
  seeded: boolean,
): AgentState => {
  const base = {
    environmentId,
    agentId,
    seeded,
    scenario: { failNextAssociation: false },
    reportedPolicyVersions: [],
  };
  if (!seeded)
    return {
      ...base,
      links: [],
      nativePolicies: [],
      mcpPolicies: [],
      mcpRules: [],
      expectations: [],
      assessment: { state: "NOT_EVALUATED", findings: [], evaluatedDataSourceIds: [] },
      gatewayPaths: [],
    };
  return {
    ...base,
    links: clone(LINKS),
    nativePolicies: clone(NATIVE_POLICIES),
    mcpPolicies: clone(MCP_POLICIES),
    mcpRules: clone(MCP_RULES),
    expectations: clone(EXPECTATIONS),
    assessment: {
      state: "CURRENT",
      assessedAt: SEED_ASSESSED_AT,
      findings: evaluateExpectations(EXPECTATIONS, NATIVE_POLICIES, dataSourceId =>
        rolesForSource(dataSourceId),
      ),
      evaluatedDataSourceIds: [IDS.customerDatabase],
    },
    gatewayPaths: clone(GATEWAY_PATHS),
  };
};

function rolesForSource(
  dataSourceId: string,
  extra: RegisteredDataSource[] = [],
): NativeRole[] {
  const source = [...DATA_SOURCE_REGISTRY, ...extra].find(
    item => item.id === dataSourceId,
  );
  return source ? (NATIVE_ROLES[source.platform] ?? []) : [];
}

/* ------------------------------------------------------------------ store */

/** Bumped whenever the persisted shape changes; older snapshots are dropped. */
const SNAPSHOT_SCHEMA = 2;

interface Snapshot {
  schema: typeof SNAPSHOT_SCHEMA;
  clock: number;
  agents: AgentState[];
}

const isSnapshot = (value: unknown): value is Snapshot =>
  isRecord(value) &&
  value.schema === SNAPSHOT_SCHEMA &&
  typeof value.clock === "number" &&
  Array.isArray(value.agents) &&
  value.agents.every(
    agent =>
      isRecord(agent) &&
      typeof agent.environmentId === "string" &&
      typeof agent.agentId === "string" &&
      Array.isArray(agent.links) &&
      Array.isArray(agent.nativePolicies) &&
      Array.isArray(agent.mcpRules) &&
      Array.isArray(agent.gatewayPaths),
  );

class AgentProtectionStore {
  private agents = new Map<string, AgentState>();
  private previews = new Map<string, { agentId: string; policyIds: string[] }>();
  private readonly clock = new DemoClock();
  private sessionScope?: string;

  constructor() {
    // Authentication: forget memory, never write the old user's state.
    registerAgentSessionReset("protection", () => this.clearMemory());
    // Protection state describes estate agents; it never outlives the estate.
    onEstateReset(() => this.reset());
    // The agent's authored MCP rules may name any service this tab shows.
    authoredAgentPolicyStore.registerServerResolver((environmentId, agentId) => {
      try {
        return this.view(this.state({ environmentId, agentId })).mcpServices;
      } catch {
        // Not an agent this store can project: the estate record decides.
        return [];
      }
    });
  }

  /* ------------------------------------------------------------ session */

  private ensureSession() {
    const scope = agentSessionScope(isInvestmentBankingDemo());
    if (this.sessionScope === scope) return;
    this.sessionScope = scope;
    this.agents.clear();
    this.clock.reset();
    const saved = readAgentSession("protection", scope, isSnapshot);
    if (!saved) return;
    this.clock.value = saved.clock;
    saved.agents.forEach(agent => this.agents.set(agent.agentId, agent));
  }

  private persist() {
    if (!this.sessionScope) return;
    writeAgentSession("protection", this.sessionScope, {
      schema: SNAPSHOT_SCHEMA,
      clock: this.clock.value,
      agents: [...this.agents.values()],
    } satisfies Snapshot);
  }

  private clearMemory() {
    this.agents.clear();
    this.previews.clear();
    this.clock.reset();
    this.sessionScope = undefined;
  }

  /**
   * Explicit demo/test reset: memory and the saved session both go, so the
   * next read starts from the fixture. Also runs on every estate reset.
   */
  reset() {
    this.clearMemory();
    try {
      sessionStorage.removeItem(AGENT_SESSION_KEYS.protection);
    } catch {
      // Storage can be blocked; memory is already clear.
    }
  }

  /**
   * The agent's owned state, seeded or created empty on first read. Like the
   * live API, a read without an environment finds the agent's own.
   */
  private state({ environmentId, agentId }: AgentProtectionQuery): AgentState {
    this.ensureSession();
    const existing = this.agents.get(agentId);
    if (existing) return existing;
    const resolved = environmentId || agentEstateStore.environmentOf(agentId);
    if (!resolved || !agentEstateStore.getAgent(resolved, agentId))
      return invalid(`Unknown agent: ${agentId}`);
    const created = ownedState(resolved, agentId, isSeededAgentId(agentId));
    this.agents.set(agentId, created);
    return created;
  }

  /**
   * The estate's current view of the agent - never cached. The seeded agent's
   * fixture is its discovered story, so from the estate it takes only what an
   * administrator connected.
   */
  private estate(state: AgentState): EstateView {
    const agent = agentEstateStore.getAgent(state.environmentId, state.agentId);
    if (!agent) return invalid(`Unknown agent: ${state.agentId}`);
    return fromEstate(
      state.seeded
        ? {
            ...agent,
            mcpServices: agent.mcpServices.filter(
              service => service.connectionOrigin === "MANUAL",
            ),
          }
        : agent,
    );
  }

  /**
   * Every link the agent has: the estate's, with this store's edits laid
   * over them, then the links only this store records.
   */
  private links(state: AgentState, estate = this.estate(state)): AgentResourceLink[] {
    const owned = new Map(state.links.map(link => [linkKey(link), link]));
    const derivedKeys = new Set(estate.links.map(linkKey));
    return [
      ...estate.links.map(link => {
        const edit = owned.get(linkKey(link));
        return edit
          ? {
              ...link,
              accessPath: edit.accessPath,
              linkedMcpServiceIds: edit.linkedMcpServiceIds,
              note: edit.note,
            }
          : link;
      }),
      // An edit to a discovered link the estate no longer reports lapses.
      ...state.links.filter(
        link =>
          !derivedKeys.has(linkKey(link)) &&
          (state.seeded || link.origin === "MANUAL"),
      ),
    ];
  }

  private nameOf(state: AgentState): string {
    return (
      agentEstateStore.getAgent(state.environmentId, state.agentId)?.name ??
      state.agentId
    );
  }

  private gatewayPaths(state: AgentState, estate = this.estate(state)): GatewayPath[] {
    const owned = new Map(state.gatewayPaths.map(path => [path.mcpServiceId, path]));
    const derivedIds = new Set(estate.gatewayPaths.map(path => path.mcpServiceId));
    return [
      ...estate.gatewayPaths.map(path => owned.get(path.mcpServiceId) ?? path),
      ...state.gatewayPaths.filter(path => !derivedIds.has(path.mcpServiceId)),
    ];
  }

  /** Every write goes through here: mutate, mark stale where asked, persist. */
  private write<T>(
    query: AgentProtectionQuery,
    mutate: (state: AgentState) => T,
    { stale = true }: { stale?: boolean } = {},
  ): T {
    const state = this.state(query);
    const result = mutate(state);
    if (stale && state.assessment.state === "CURRENT")
      state.assessment = { ...state.assessment, state: "STALE" };
    this.persist();
    return clone(result);
  }

  private mcpRegistry(state: AgentState, estate = this.estate(state)) {
    const byId = new Map<string, RegisteredMcpService>();
    [...(state.seeded ? MCP_REGISTRY : []), ...estate.mcp].forEach(item =>
      byId.set(item.id, item),
    );
    return byId;
  }

  private sourceRegistry(state: AgentState, estate = this.estate(state)) {
    const byId = new Map<string, RegisteredDataSource>();
    [...DATA_SOURCE_REGISTRY, ...estate.sources].forEach(item =>
      byId.set(item.id, item),
    );
    return byId;
  }

  private roles(state: AgentState, dataSourceId: string) {
    return rolesForSource(dataSourceId, this.estate(state).sources);
  }

  /* --------------------------------------------------------------- reads */

  getProtection(query: AgentProtectionQuery): AgentProtection {
    return this.view(this.state(query));
  }

  /** The resolved record: links joined to the registry, counts left to the UI. */
  private view(state: AgentState): AgentProtection {
    const estate = this.estate(state);
    return clone(
      joinProtection({
        agentId: state.agentId,
        agentName: estate.name,
        links: this.links(state, estate),
        mcpById: this.mcpRegistry(state, estate),
        sourceById: this.sourceRegistry(state, estate),
        gatewayPaths: this.gatewayPaths(state, estate),
        own: state,
      }),
    );
  }

  /**
   * What Add can offer: the MCP Registry catalog (plus, for the seeded agent,
   * its fixture tenant's services) and every registered data source. Anything
   * the agent already has - or the estate already reports - is marked added.
   */
  listCandidates(query: AgentProtectionQuery): ProtectionCandidates {
    const state = this.state(query);
    const estate = this.estate(state);
    const linked = new Set(this.links(state, estate).map(linkKey));
    const reached = new Set(
      (agentEstateStore.getAgent(state.environmentId, state.agentId)?.mcpServices ?? []).map(
        service => service.id,
      ),
    );
    const mcp = new Map<string, RegisteredMcpService>();
    [
      ...(state.seeded ? MCP_REGISTRY : []),
      ...agentEstateStore.listMcpCatalog().map(toRegistered),
    ].forEach(item => mcp.set(item.id, item));
    return clone({
      mcpServices: [...mcp.values()].map(resource => ({
        resource,
        alreadyAdded:
          linked.has(linkKey({ resourceKind: "MCP_SERVICE", resourceId: resource.id })) ||
          reached.has(resource.id),
      })),
      dataSources: [...this.sourceRegistry(state, estate).values()].map(resource => ({
        resource,
        alreadyAdded: linked.has(
          linkKey({ resourceKind: "DATA_SOURCE", resourceId: resource.id }),
        ),
      })),
    });
  }

  getCatalog(query: AgentProtectionQuery & { dataSourceId: string }): DataSourceCatalog {
    const state = this.state(query);
    return clone({
      dataSourceId: query.dataSourceId,
      objects: DATA_SOURCE_OBJECTS[query.dataSourceId] ?? [],
      roles: this.roles(state, query.dataSourceId),
    });
  }

  /* --------------------------------------------------------- association */

  associate(request: AssociateResourcesRequest): AssociationResult[] {
    return this.write(request, state => {
      const isMcp = request.resourceKind === "MCP_SERVICE";
      const estate = this.estate(state);
      const links = this.links(state, estate);
      // A registry server is connected in the estate (one record for every
      // screen); only the seeded fixture's own services are linked here.
      const fixtureMcp = state.seeded ? FIXTURE_MCP_IDS : new Set<string>();
      const registered = isMcp
        ? new Set([
            ...fixtureMcp,
            ...agentEstateStore.listMcpCatalog().map(server => server.id),
          ])
        : new Set(this.sourceRegistry(state, estate).keys());
      const at = this.clock.now();
      // The scripted partial failure: one item (Support Archive when it is in
      // the batch, else the last one) fails once, then succeeds on retry.
      const failId = state.scenario.failNextAssociation
        ? (request.resourceIds.find(id => id === IDS.supportArchive) ??
          request.resourceIds.at(-1))
        : undefined;
      const results = request.resourceIds.map((resourceId): AssociationResult => {
        if (!registered.has(resourceId))
          return { resourceId, outcome: "FAILED", message: "Not registered in TrustLogix." };
        const exists = links.some(
          link => link.resourceKind === request.resourceKind && link.resourceId === resourceId,
        );
        if (exists) return { resourceId, outcome: "ALREADY_ADDED" };
        if (resourceId === failId)
          return {
            resourceId,
            outcome: "FAILED",
            message: "The association could not be recorded (simulated failure). Retry to try again.",
          };
        if (isMcp && !fixtureMcp.has(resourceId)) {
          const { failed } = agentEstateStore.bindMcpServers({
            environmentId: state.environmentId,
            agentId: state.agentId,
            mcpServerIds: [resourceId],
            actor: request.actor,
          });
          if (!failed.length) return { resourceId, outcome: "ADDED" };
          return failed[0].reason === "ALREADY_CONNECTED"
            ? { resourceId, outcome: "ALREADY_ADDED" }
            : { resourceId, outcome: "FAILED", message: "Not registered in TrustLogix." };
        }
        state.links.push({
          resourceKind: request.resourceKind,
          resourceId,
          origin: "MANUAL",
          verification: "NOT_VERIFIED",
          ...(!isMcp && {
            accessPath: "NOT_SPECIFIED",
            linkedMcpServiceIds: [],
          }),
          addedAt: at,
          addedBy: request.actor,
        });
        if (isMcp)
          state.gatewayPaths.push({
            mcpServiceId: resourceId,
            setup: "CONFIGURED",
            verification: "PENDING",
          });
        return { resourceId, outcome: "ADDED" };
      });
      if (failId) state.scenario = { ...state.scenario, failNextAssociation: false };
      return results;
    });
  }

  updateAssociation(request: UpdateAssociationRequest): AgentResourceLink {
    return this.write(request, state => {
      const links = this.links(state);
      const current = links.find(
        item => item.resourceKind === "DATA_SOURCE" && item.resourceId === request.dataSourceId,
      );
      if (!current) return invalid("This data source is not associated with the agent.");
      const associatedMcp = new Set(
        links
          .filter(item => item.resourceKind === "MCP_SERVICE")
          .map(item => item.resourceId),
      );
      const linked = request.linkedMcpServiceIds.filter(id => associatedMcp.has(id));
      if (request.accessPath === "THROUGH_MCP" && !linked.length)
        invalid("Select at least one MCP service already associated with this agent.");
      // Origin and verification are deliberately untouched: an administrator's
      // description of the path is not evidence that it exists.
      const next: AgentResourceLink = {
        ...current,
        accessPath: request.accessPath,
        linkedMcpServiceIds: request.accessPath === "THROUGH_MCP" ? linked : [],
        note: request.note?.trim() || undefined,
      };
      // Stored as this store's edit, whether the link itself is owned or derived.
      state.links = [
        ...state.links.filter(item => linkKey(item) !== linkKey(next)),
        next,
      ];
      return next;
    });
  }

  removeAssociation(request: RemoveAssociationRequest): { resourceId: string } {
    return this.write(request, state => {
      const estate = this.estate(state);
      const link = this.links(state, estate).find(
        item => item.resourceKind === request.resourceKind && item.resourceId === request.resourceId,
      );
      if (!link) return invalid("This resource is not associated with the agent.");
      if (link.origin === "DISCOVERED")
        invalid("A discovered association is re-added by the next registry sync and cannot be removed here.");
      const isMcp = request.resourceKind === "MCP_SERVICE";
      if (isMcp) {
        // Authored rules (drafts included) naming the server would be left
        // pointing at a server the agent no longer reaches, and every later
        // save of their policy would fail. They have to change first.
        const blocking = authoredAgentPolicyStore.policiesNamingServer(
          state.agentId,
          request.resourceId,
        );
        if (blocking.length)
          invalid(
            `Policy rules still target this MCP service. Remove or retarget them first in: ${blocking
              .map(policy => `"${policy.name}"`)
              .join(", ")}.`,
          );
        if (estate.links.some(item => linkKey(item) === linkKey(link)))
          agentEstateStore.unbindMcpServer({
            environmentId: state.environmentId,
            agentId: state.agentId,
            mcpServerId: request.resourceId,
          });
      }
      // Only the relationship goes: the resource stays registered, its native
      // policies and the agent's MCP rules are kept.
      state.links = state.links.filter(item => linkKey(item) !== linkKey(link));
      if (isMcp) {
        state.gatewayPaths = state.gatewayPaths.filter(
          path => path.mcpServiceId !== request.resourceId,
        );
        state.links.forEach(item => {
          if (item.linkedMcpServiceIds)
            item.linkedMcpServiceIds = item.linkedMcpServiceIds.filter(
              id => id !== request.resourceId,
            );
        });
      }
      return { resourceId: request.resourceId };
    });
  }

  /* ---------------------------------------------------------- tool rules */

  /* ----------------------------------------------------- native policies */

  saveNativeDraft(request: SaveNativePolicyRequest): NativePolicy {
    return this.write(request, state => {
      const input = request.policy;
      const source = this.view(state).dataSources.find(
        item => item.id === input.dataSourceId,
      );
      if (!source) return invalid("The data source is not associated with this agent.");
      if (!source.nativePolicySupported)
        invalid(`TrustLogix cannot author native policies on ${source.platform}.`);
      const name = input.name.trim();
      if (!name) invalid("A policy name is required.");
      const duplicate = state.nativePolicies.some(
        policy =>
          policy.dataSourceId === input.dataSourceId &&
          policy.id !== input.id &&
          policy.name.toLowerCase() === name.toLowerCase(),
      );
      if (duplicate) invalid(`A policy named "${name}" already exists on ${source.name}.`);
      const existing = input.id
        ? state.nativePolicies.find(policy => policy.id === input.id)
        : undefined;
      if (existing?.status === "DEPLOYED")
        invalid("A deployed policy is not edited from the agent; open it in policy management.");
      const next: NativePolicy = {
        id: existing?.id ?? `pol-${this.clock.value + 1}-${input.type.toLowerCase()}`,
        dataSourceId: input.dataSourceId,
        type: input.type,
        name,
        description: input.description?.trim() || undefined,
        targetObjectIds: input.targetObjectIds,
        targetColumns: input.targetColumns,
        privileges: input.privileges,
        principal: input.principalName
          ? {
              type: "ROLE",
              name: input.principalName,
              mappingSource: "ADMINISTRATOR",
              verification: input.principalVerification ?? "NOT_VERIFIED",
            }
          : undefined,
        combinator: input.combinator,
        conditions: input.conditions,
        // Editing a failed policy makes it a draft again; its error stays on
        // the run, not on the edited definition.
        status: "DRAFT",
        authoredFromAgentId: state.agentId,
        updatedAt: this.clock.now(),
      };
      state.nativePolicies = existing
        ? state.nativePolicies.map(policy => (policy.id === existing.id ? next : policy))
        : [...state.nativePolicies, next];
      return next;
    });
  }

  verifyMapping(request: VerifyMappingRequest) {
    const state = this.state(request);
    const role = this.roles(state, request.dataSourceId).find(
      item => item.name === request.roleName,
    );
    return clone({
      roleName: request.roleName,
      verification: role && !role.stale ? ("VERIFIED" as const) : ("FAILED" as const),
    });
  }

  private deployError(state: AgentState, policy: NativePolicy): DeploymentError | undefined {
    const source = this.sourceRegistry(state).get(policy.dataSourceId);
    const role = this.roles(state, policy.dataSourceId).find(
      item => item.name === policy.principal?.name,
    );
    if (role && !role.stale) return undefined;
    return {
      code: "INVALID_ROLE_MAPPING",
      message: `${source?.platform ?? "The platform"} rejected the policy: role ${policy.principal?.name ?? "(none)"} does not exist or is not authorized.`,
      remediation:
        "Choose a valid native role, verify the mapping, then retry the failed operation.",
    };
  }

  private runNative(state: AgentState, policy: NativePolicy): DeploymentOperation["status"] {
    const error = this.deployError(state, policy);
    policy.status = error ? "FAILED" : "DEPLOYED";
    policy.lastError = error;
    policy.updatedAt = this.clock.now();
    return policy.status === "DEPLOYED" ? "SUCCEEDED" : "FAILED";
  }

  deploy(request: DeployChangesRequest): DeploymentRun {
    return this.write(request, state => {
      const policy = state.nativePolicies.find(item => item.id === request.nativePolicyId);
      if (!policy) return invalid("Unknown native policy.");
      if (policy.status === "DEPLOYED") invalid("This policy is already deployed.");
      if (!policy.principal) invalid("Select a native role before deploying.");
      if (!policy.targetObjectIds.length) invalid("Select at least one target object.");
      if (policy.type === "ROW_ACCESS" && !policy.conditions.length)
        invalid("A Row Access policy needs at least one condition.");
      const startedAt = this.clock.now();
      const reported = new Set(state.reportedPolicyVersions);
      const changed = authoredAgentPolicyStore
        .list({ environmentId: state.environmentId, agentId: state.agentId })
        .filter(
          item =>
            item.status === "APPLIED" &&
            !reported.has(`${item.id}@${item.version}`),
        );
      const ruleOps: DeploymentOperation[] = changed.map(item => ({
        id: `op-rule-${item.id}`,
        kind: "MCP_RULE" as const,
        targetId: item.id,
        label: item.name,
        attempted: true,
        status: "SUCCEEDED" as const,
        attempts: 1,
      }));
      const nativeStatus = this.runNative(state, policy);
      const nativeOp: DeploymentOperation = {
        id: `op-native-${policy.id}`,
        kind: "NATIVE_POLICY",
        targetId: policy.id,
        label: policy.name,
        attempted: true,
        status: nativeStatus,
        attempts: 1,
        error: policy.lastError,
      };
      const unchanged: DeploymentOperation[] = state.nativePolicies
        .filter(
          item =>
            item.id !== policy.id &&
            item.dataSourceId === policy.dataSourceId &&
            item.status === "DEPLOYED",
        )
        .map((item): DeploymentOperation => ({
          id: `op-existing-${item.id}`,
          kind: "EXISTING_UNCHANGED",
          targetId: item.id,
          label: item.name,
          attempted: false,
          status: "UNCHANGED",
          attempts: 0,
        }));
      state.reportedPolicyVersions = [
        ...state.reportedPolicyVersions,
        ...changed.map(item => `${item.id}@${item.version}`),
      ];
      state.lastRun = {
        id: `run-${this.clock.value}`,
        startedAt,
        completedAt: this.clock.now(),
        operations: [...ruleOps, nativeOp, ...unchanged],
      };
      return state.lastRun;
    });
  }

  retryFailed(request: RetryFailedRequest): DeploymentRun {
    return this.write(request, state => {
      const run = state.lastRun;
      if (!run || run.id !== request.runId)
        return invalid("This deployment run is no longer current. Reopen the results.");
      // Only failed operations are retried; a retry never creates a policy.
      run.operations = run.operations.map(operation => {
        if (operation.status !== "FAILED" || operation.kind !== "NATIVE_POLICY")
          return operation;
        const policy = state.nativePolicies.find(item => item.id === operation.targetId);
        if (!policy) return operation;
        const status = this.runNative(state, policy);
        return {
          ...operation,
          status,
          attempts: operation.attempts + 1,
          error: policy.lastError,
          label: policy.name,
        };
      });
      run.completedAt = this.clock.now();
      return run;
    });
  }

  /* -------------------------------------------- assessment and enforcement */

  reassess(query: AgentProtectionQuery): ProtectionAssessment {
    return this.write(
      query,
      state => {
        const associated = new Set(
          this.links(state)
            .filter(link => link.resourceKind === "DATA_SOURCE")
            .map(link => link.resourceId),
        );
        state.assessment = {
          state: "CURRENT",
          assessedAt: this.clock.now(),
          findings: evaluateExpectations(
            state.expectations.filter(item => associated.has(item.dataSourceId)),
            state.nativePolicies,
            id => this.roles(state, id),
          ),
          evaluatedDataSourceIds: [...associated],
        };
        return state.assessment;
      },
      { stale: false },
    );
  }

  gatewayCheck(request: GatewayCheckRequest): GatewayPath {
    return this.write(
      request,
      state => {
        const recorded = this.gatewayPaths(state).find(
          item => item.mcpServiceId === request.mcpServiceId,
        );
        if (!recorded) return invalid("No gateway path is recorded for this MCP service.");
        // A check is this store's record, laid over a derived path.
        const path = { ...recorded };
        state.gatewayPaths = [
          ...state.gatewayPaths.filter(item => item.mcpServiceId !== path.mcpServiceId),
          path,
        ];
        if (path.setup !== "CONFIGURED")
          invalid("Configure the gateway for this MCP service before verifying it.");
        if (request.check === "BYPASS") path.verification = "CONFIRMED_BYPASS";
        // A confirmed bypass is only cleared by an explicit path correction.
        else if (request.check === "RESOLVE" || path.verification !== "CONFIRMED_BYPASS")
          path.verification = "VERIFIED";
        path.checkedAt = this.clock.now();
        return path;
      },
      { stale: false },
    );
  }

  setScenario(request: ScenarioRequest): ProtectionScenario {
    return this.write(
      request,
      state => {
        state.scenario = { ...state.scenario, ...request.scenario };
        return state.scenario;
      },
      { stale: false },
    );
  }

  /**
   * Demo "Reset scenario": the agent's protection state, the policies its
   * suggestions created (so they are offered again), and the MCP connections
   * made here - except one an authored rule still names, which would be
   * orphaned.
   */
  resetAgent(query: AgentProtectionQuery): AgentProtection {
    this.ensureSession();
    authoredAgentPolicyStore.removeRecommendationPolicies(query.agentId);
    const environmentId =
      query.environmentId || agentEstateStore.environmentOf(query.agentId);
    if (environmentId)
      agentEstateStore
        .getAgent(environmentId, query.agentId)
        ?.mcpServices.filter(
        service =>
          service.connectionOrigin === "MANUAL" &&
          !authoredAgentPolicyStore.policiesNamingServer(query.agentId, service.id)
            .length,
      )
      .forEach(service =>
          agentEstateStore.unbindMcpServer({
            environmentId,
            agentId: query.agentId,
            mcpServerId: service.id,
          }),
        );
    this.agents.delete(query.agentId);
    this.previews.clear();
    if (this.agents.size === 0) this.clock.reset();
    this.persist();
    return this.getProtection(query);
  }

  /* ----------------------------------------------------- recommendations */

  /** The seeded agent, when a recommendation scope is exactly that agent. */
  private seededScope(params: {
    environmentId: string;
    scope: ProtectionRecommendationQuery["scope"];
  }): AgentState | undefined {
    const { scope } = params;
    if (scope.kind !== "AGENTS" || scope.agentIds.length !== 1) return undefined;
    const [agentId] = scope.agentIds;
    if (!isSeededAgentId(agentId)) return undefined;
    return this.state({ environmentId: params.environmentId, agentId });
  }

  /** Suggestions already applied: authored policies created from them. */
  private appliedRecommendationIds(state: AgentState) {
    return new Set(
      authoredAgentPolicyStore
        .list({ environmentId: "", agentId: state.agentId })
        .flatMap(policy => (policy.recommendationId ? [policy.recommendationId] : [])),
    );
  }

  private readinessCounts(state: AgentState, mcpServiceId: string): EnforcementReadinessCounts {
    const path = this.gatewayPaths(state).find(item => item.mcpServiceId === mcpServiceId);
    return {
      ready: path?.verification === "VERIFIED" ? 1 : 0,
      setupRequired: path?.setup === "NOT_CONFIGURED" ? 1 : 0,
      attentionRequired: path?.verification === "CONFIRMED_BYPASS" ? 1 : 0,
      unknown:
        !path || (path.setup === "CONFIGURED" && path.verification === "PENDING")
          ? 1
          : 0,
    };
  }

  private recommendationFor(state: AgentState, seeded: (typeof RECOMMENDATIONS)[number]) {
    const service = MCP_REGISTRY.find(item => item.id === seeded.rule.mcpServiceId);
    return {
      ...seeded.recommendation,
      appliesTo: {
        agentCount: 1,
        label: `${this.nameOf(state)} · ${service?.name ?? ""}`,
      },
      targets: { mcpServerCount: 1, toolCount: 1, dataSourceCount: 0, controlCount: 0 },
      enforcementReadiness: this.readinessCounts(state, seeded.rule.mcpServiceId),
    } satisfies AgentPolicyRecommendation;
  }

  /** Only recommendations whose MCP service is still on the agent are offered. */
  private offered(state: AgentState) {
    const linked = new Set(
      this.links(state)
        .filter(link => link.resourceKind === "MCP_SERVICE")
        .map(link => link.resourceId),
    );
    return RECOMMENDATIONS.filter(item => linked.has(item.rule.mcpServiceId));
  }

  resolveRecommendations(
    params: ProtectionRecommendationQuery,
  ): AgentPolicyRecommendation[] | undefined {
    const state = this.seededScope(params);
    if (!state) return undefined;
    const applied = this.appliedRecommendationIds(state);
    return clone(
      this.offered(state)
        .filter(item => !applied.has(item.recommendation.id))
        .map(item => this.recommendationFor(state, item)),
    );
  }

  resolveCoverage(params: ProtectionRecommendationQuery): AgentPolicyCoverage | undefined {
    const state = this.seededScope(params);
    if (!state) return undefined;
    const applied = this.appliedRecommendationIds(state);
    return clone({
      scopeAgentCount: 1,
      policies: RECOMMENDATIONS.filter(item => applied.has(item.recommendation.id)).map(
        item => ({
          policyId: item.recommendation.id,
          name: item.recommendation.name,
          state: "ACTIVE" as const,
          agentIds: [state.agentId],
          controls: item.recommendation.controls,
        }),
      ),
    });
  }

  private seededRecommendations(policyIds: string[]) {
    const found = RECOMMENDATIONS.filter(item => policyIds.includes(item.recommendation.id));
    return found.length === policyIds.length && found.length > 0 ? found : undefined;
  }

  private readiness(environmentId: string, agentId: string): EnforcementReadiness {
    return (
      agentEstateStore.getAgent(environmentId, agentId)?.enforcementReadiness ?? {
        status: "UNKNOWN",
        method: "MCP_GATEWAY",
        environmentConfigured: true,
        agentRoutedThroughEnforcement: true,
        routingObserved: false,
      }
    );
  }

  getPolicyReview(params: PolicyReviewQuery): PolicyReview | undefined {
    const state = this.seededScope(params);
    const seeded = state && this.seededRecommendations(params.policyIds);
    if (!state || !seeded) return undefined;
    const applied = this.appliedRecommendationIds(state);
    const pending = seeded
      .filter(item => !applied.has(item.recommendation.id))
      .map(item => item.recommendation.id);
    const counts = seeded
      .map(item => this.readinessCounts(state, item.rule.mcpServiceId))
      .reduce(
        (sum, item) => ({
          ready: Math.max(sum.ready, item.ready),
          setupRequired: Math.max(sum.setupRequired, item.setupRequired),
          attentionRequired: Math.max(sum.attentionRequired, item.attentionRequired),
          unknown: Math.max(sum.unknown, item.unknown),
        }),
        { ready: 0, setupRequired: 0, attentionRequired: 0, unknown: 0 },
      );
    return clone({
      scopeAgentCount: 1,
      viewedAgentId: state.agentId,
      enforcementScope: pending.length ? "TARGETED" : "ASSIGNED",
      enforcement: counts,
      policies: seeded.map(({ recommendation }) => ({
        policyId: recommendation.id,
        name: recommendation.name,
        category: recommendation.category,
        isDraft: false,
        intentBased: false,
        frameworks: recommendation.frameworks,
        recommendation: {
          strength: recommendation.strength,
          rationale: recommendation.whyRecommended,
        },
        applicability: "RECOMMENDED" as const,
        summary: recommendation.outcome,
        intent: {
          allow: recommendation.controls.filter(c => c.sense === "ALLOW").map(c => c.label),
          restrict: recommendation.controls.filter(c => c.sense === "BLOCK").map(c => c.label),
        },
        controls: recommendation.controls,
        policyState: applied.has(recommendation.id) ? ("APPLIED" as const) : ("NOT_APPLIED" as const),
        enforcementMethod: "MCP tool policy (rule in the agent's MCP access policy)",
      })),
      agents: [
        {
          id: state.agentId,
          name: this.nameOf(state),
          applicability: "RECOMMENDED" as const,
          policyIds: seeded.map(item => item.recommendation.id),
          pendingPolicyIds: pending,
          readiness: this.readiness(params.environmentId, state.agentId),
          targets: {
            mcpServerCount: unique(seeded.map(item => item.rule.mcpServiceId)).length,
            toolCount: seeded.length,
            dataSourceCount: 0,
            controlCount: 0,
          },
        },
      ],
      targets: seeded.map(({ recommendation, rule }) => ({
        policyId: recommendation.id,
        mcpServiceIds: [rule.mcpServiceId],
        toolKeys: [`${rule.mcpServiceId}::${rule.toolId}`],
        dataSourceIds: [],
        dataControlCount: 0,
      })),
    } satisfies PolicyReview);
  }

  previewPolicies(params: ApplyPoliciesRequest): PolicyApplicationPreview | undefined {
    const state = this.seededScope(params);
    const seeded = state && this.seededRecommendations(params.policyIds);
    if (!state || !seeded) return undefined;
    const applied = this.appliedRecommendationIds(state);
    const id = `preview-${state.agentId}-${this.clock.now()}`;
    this.previews.set(id, { agentId: state.agentId, policyIds: params.policyIds });
    const affected = seeded.some(item => !applied.has(item.recommendation.id)) ? 1 : 0;
    const agent = agentEstateStore.getAgent(params.environmentId, state.agentId);
    const status = agent?.securityStatus ?? "NOT_SECURE";
    return clone({
      id,
      scopeAgentCount: 1,
      affectedAgentCount: affected,
      newlySecureCount: 0,
      enforcement: this.readinessCounts(state, seeded[0].rule.mcpServiceId),
      policies: seeded.map(({ recommendation }) => {
        const already = applied.has(recommendation.id);
        return {
          policyId: recommendation.id,
          policyName: recommendation.name,
          reason: recommendation.whyRecommended,
          outcome: recommendation.outcome,
          agents: already
            ? []
            : [
                {
                  id: state.agentId,
                  name: this.nameOf(state),
                  gaps: [],
                  statusBefore: status,
                  statusAfter: status,
                  remainingGapCount: 0,
                },
              ],
          excludedAgentCount: already ? 1 : 0,
          exclusions: already
            ? [{ id: state.agentId, name: this.nameOf(state), reason: "ALREADY_ASSIGNED" as const }]
            : [],
          alreadyAppliedCount: already ? 1 : 0,
        };
      }),
    } satisfies PolicyApplicationPreview);
  }

  /**
   * Whether `applyPolicies` handles this request, throwing whatever it would
   * refuse - without writing. A mixed review checks the fixture half here
   * before the estate half commits, so one apply never half-lands.
   */
  checkApply(params: ApplyPoliciesRequest): boolean {
    const state = this.seededScope(params);
    const seeded = state && this.seededRecommendations(params.policyIds);
    if (!state || !seeded) return false;
    if (!params.previewId || !this.previews.has(params.previewId))
      invalid("Review the policy scope before applying.");
    const applied = this.appliedRecommendationIds(state);
    const reached = new Set(this.view(state).mcpServices.map(service => service.id));
    if (
      seeded.some(
        item =>
          !applied.has(item.recommendation.id) &&
          !reached.has(item.rule.mcpServiceId),
      )
    )
      invalid(
        "A suggested improvement targets an MCP service this agent no longer reaches. Review the suggestions again.",
      );
    return true;
  }

  applyPolicies(params: ApplyPoliciesRequest): ApplyPoliciesResult | undefined {
    const state = this.seededScope(params);
    const seeded = state && this.seededRecommendations(params.policyIds);
    if (!state || !seeded) return undefined;
    this.checkApply(params);
    const query = { environmentId: params.environmentId, agentId: state.agentId };
    // Each suggestion becomes an applied tool policy in the agent's authored
    // policies - the same records Create Policy Rule writes.
    const appliedAt = this.write(query, current => {
      const applied = this.appliedRecommendationIds(current);
      const at = this.clock.now();
      seeded
        .filter(item => !applied.has(item.recommendation.id))
        .forEach(({ recommendation, rule }) =>
          authoredAgentPolicyStore.create({
            ...query,
            status: "APPLIED",
            actor: "Suggested improvement",
            policy: {
              name: rule.name,
              description: recommendation.outcome,
              origin: "TOOL_CUSTOMIZATION",
              inheritedPolicyIds: [],
              recommendationId: recommendation.id,
              rules: [
                {
                  name: rule.name,
                  mcpServerId: rule.mcpServiceId,
                  toolIds: [rule.toolId],
                  effect: rule.effect,
                  combinator: "AND",
                  conditions: [],
                },
              ],
            },
          }),
        );
      return at;
    });
    this.previews.delete(params.previewId!);
    const coverage = {
      totalAgents: 1,
      secureCount: 0,
      notSecureCount: 1,
      notEvaluatedCount: 0,
      securePercent: 0,
    };
    return clone({
      appliedAt,
      sourceAgentCount: 1,
      targetedAgentCount: 1,
      scopeAgentCount: 1,
      newlySecureCount: 0,
      coverageBefore: coverage,
      coverageAfter: coverage,
      enforcement: this.readinessCounts(state, seeded[0].rule.mcpServiceId),
      results: seeded.map(({ recommendation, rule }) => ({
        policyId: recommendation.id,
        policyName: recommendation.name,
        appliedAt,
        scopeAgentCount: 1,
        newlySecureCount: 0,
        contextLabel: this.nameOf(state),
        enforcement: this.readinessCounts(state, rule.mcpServiceId),
      })),
    } satisfies ApplyPoliciesResult);
  }
}

export const agentProtectionStore = new AgentProtectionStore();
